import { Component, Input } from '@angular/core';
import { DocumentUploadEntry, DocumentUploadStatus } from './document-upload';

const STATUS_LABELS: Record<DocumentUploadStatus, string> = {
  pending: 'Pending',
  uploading: 'Uploading…',
  done: 'Uploaded',
  failed: 'Failed',
  processing: 'Processing',
};

@Component({
  selector: 'app-document-upload-list',
  template: `
    <ul class="document-upload-list">
      @for (entry of entries; track $index) {
        <li class="document-upload-item" [attr.data-status]="entry.status">
          <span class="document-upload-icon" aria-hidden="true">
            @switch (entry.status) {
              @case ('uploading') {
                <span class="document-upload-spinner"></span>
              }
              @case ('done') {
                <i class="ki-filled ki-check-circle"></i>
              }
              @case ('failed') {
                <i class="ki-filled ki-cross-circle"></i>
              }
              @case ('processing') {
                <i class="ki-filled ki-information-2"></i>
              }
              @default {
                <i class="ki-filled ki-time"></i>
              }
            }
          </span>
          <div class="document-upload-text">
            <span class="document-upload-name">{{ entry.file.name }}</span>
            @if (entry.message) {
              <span class="document-upload-message">{{ entry.message }}</span>
            }
          </div>
          <span class="document-upload-status">{{ statusLabel(entry.status) }}</span>
        </li>
      }
    </ul>
  `,
  styles: `
    .document-upload-list {
      display: grid;
      gap: 0.375rem;
      font-size: 0.875rem;
    }

    .document-upload-item {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      color: var(--color-secondary-foreground, #6b7280);
    }

    .document-upload-icon {
      display: inline-flex;
      width: 1rem;
      height: 1.25rem;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .document-upload-spinner {
      width: 0.875rem;
      height: 0.875rem;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-radius: 9999px;
      animation: document-upload-spin 0.8s linear infinite;
    }

    .document-upload-text {
      display: grid;
      min-width: 0;
      flex: 1;
    }

    .document-upload-name {
      word-break: break-all;
      color: var(--color-foreground, inherit);
    }

    .document-upload-message {
      font-size: 0.75rem;
    }

    .document-upload-status {
      flex-shrink: 0;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .document-upload-item[data-status='uploading'] {
      color: var(--color-primary, #1b84ff);
    }

    .document-upload-item[data-status='done'] {
      color: var(--color-success, #16a34a);
    }

    .document-upload-item[data-status='failed'] {
      color: var(--color-destructive, #dc2626);
    }

    .document-upload-item[data-status='processing'] {
      color: var(--color-warning, #d97706);
    }

    @keyframes document-upload-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `,
})
export class DocumentUploadList {
  @Input({ required: true }) entries: readonly DocumentUploadEntry[] = [];

  protected statusLabel(status: DocumentUploadStatus): string {
    return STATUS_LABELS[status];
  }
}
